import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Terminal, Sun, Moon, Globe, Menu, X } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { useTheme } from './ThemeContext';

interface NavbarProps {
  currentView: string;
  onNavigate: (view: string) => void;
}

const Navbar: React.FC<NavbarProps> = ({ currentView, onNavigate }) => {
  const { t, language, toggleLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [imgError, setImgError] = useState(false);

  const navItems = [
    { name: t.nav.home, id: 'home' },
    { name: t.nav.services, id: 'services' },
    { name: t.nav.builder, id: 'builder' },
    { name: t.nav.projects, id: 'projects' },
    { name: t.nav.depot, id: 'store' },
    { name: t.nav.contact, id: 'contact' }
  ];

  const handleImageError = useCallback(() => {
    setImgError(true);
  }, []);

  const handleNavClick = (viewId: string) => {
    onNavigate(viewId);
    setMobileMenuOpen(false);
  };
  
  return (
    <>
      <motion.nav 
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, ease: [0.17, 0.67, 0.83, 0.67] }}
        className="fixed top-0 left-0 w-full z-50 border-b bg-white/70 dark:bg-[#020617]/70 backdrop-blur-md border-slate-200 dark:border-white/10 shadow-lg py-3 transition-all duration-500"
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div onClick={() => handleNavClick('home')} className="flex items-center gap-3 group cursor-pointer relative z-50">
            {/* Logo Logic */}
            {!imgError ? (
              <div className="relative h-10 flex items-center">
                 {/* Desktop Logo */}
                 <img 
                   src={theme === 'dark' ? '/assets/LogoWhite.svg' : '/assets/Logoblack.svg'}
                   alt="CoreStackDev"
                   width="32"
                   height="32"
                   onError={handleImageError}
                   className="hidden md:block h-8 w-auto object-contain transition-opacity duration-500"
                 />
  
                 {/* Mobile Logo */}
                 <img 
                   src="/assets/CBlack.svg"
                   alt="CoreStackDev"
                   width="32"
                   height="32"
                   onError={handleImageError}
                   className="md:hidden h-8 w-auto object-contain transition-all duration-500 dark:invert dark:brightness-0 dark:sepia-0"
                 />
              </div>
            ) : (
              // Fallback if images fail
              <div className="flex items-center gap-2">
                 <Terminal size={24} className="text-emerald-600 dark:text-terminal-green" />
                 <span className="font-bold text-lg text-slate-900 dark:text-white">CORESTACK</span>
              </div>
            )}
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex gap-1 items-center bg-white/50 dark:bg-terminal-black/50 p-1 rounded-full border border-slate-200 dark:border-white/5 backdrop-blur-sm">
            {navItems.map((item) => {
              const isActive = currentView === item.id;
              return (
                <button 
                  key={item.id} 
                  onClick={() => handleNavClick(item.id)}
                  className={`relative px-4 py-2 text-sm transition-all duration-300 rounded-full z-10 ${
                    isActive 
                      ? 'text-emerald-700 dark:text-terminal-black font-bold' 
                      : 'text-slate-600 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-white'
                  }`}
                >
                  {item.name}
                  {isActive && (
                    <motion.div
                      layoutId="active-pill"
                      className="absolute inset-0 bg-emerald-100 dark:bg-terminal-green rounded-full -z-10 shadow-[0_0_10px_rgba(57,255,20,0.3)]"
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    />
                  )}
                </button>
              );
            })}
            
            <div className={`h-4 w-px mx-2 bg-slate-300 dark:bg-white/20`}></div>
  
            {/* Theme Toggle */}
            <button 
              onClick={toggleTheme}
              className="p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10 text-slate-600 dark:text-terminal-green transition-all"
              aria-label="Toggle Theme"
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
  
            {/* Language Toggle */}
            <button 
              onClick={toggleLanguage}
              className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-mono border border-slate-200 dark:border-white/10 hover:border-emerald-600 dark:hover:border-terminal-green text-slate-600 dark:text-white transition-all ml-1"
            >
              <Globe size={14} />
              {language === 'en' ? 'TH' : 'EN'}
            </button>
          </div>
          
          {/* Mobile Actions */}
          <div className="md:hidden flex items-center gap-4 relative z-50">
             <button 
              onClick={toggleTheme}
              className="text-slate-600 dark:text-terminal-green transition-colors duration-300"
             >
               {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
             </button>
             
             <button 
               onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
               className="text-slate-900 dark:text-white hover:text-emerald-600 dark:hover:text-terminal-green transition-colors"
             >
               {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
             </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 bg-white/95 dark:bg-terminal-black/95 backdrop-blur-xl md:hidden flex flex-col items-center justify-center space-y-8"
          >
            {navItems.map((item) => {
               const isActive = currentView === item.id;
               return (
                <button 
                  key={item.id} 
                  onClick={() => handleNavClick(item.id)}
                  className={`text-2xl font-bold transition-colors tracking-widest flex items-center gap-3 ${
                    isActive 
                      ? 'text-emerald-600 dark:text-terminal-green'
                      : 'text-slate-900 dark:text-white hover:text-emerald-600 dark:hover:text-terminal-green'
                  }`}
                >
                  {isActive && <span className="text-emerald-600 dark:text-terminal-green">{'>'}</span>}
                  {item.name}
                </button>
               );
            })}
            <button 
              onClick={() => { toggleLanguage(); setMobileMenuOpen(false); }}
              className="mt-8 flex items-center gap-2 border border-slate-300 dark:border-white/20 px-6 py-2 rounded text-slate-900 dark:text-white hover:border-emerald-600 dark:hover:border-terminal-green font-mono"
            >
              <Globe size={16} />
              {language === 'en' ? 'SWITCH TO THAI' : 'SWITCH TO ENGLISH'}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default React.memo(Navbar);